import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues, SelectFilterNames } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    AgentListLanguage,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { ALL_VALUE, getAgentList } from '../../../../React/Common/API/ItemsGetters'
import {
    TELEPHONY_SYSTEM_TYPE_BROADSOFT_BROADWORKS,
    TELEPHONY_SYSTEM_TYPE_CISCO_WEBEX_CALLING
} from '../../../../NonReact/constants/TelephonyServerTypes'
import { AgentListItem, AgentListResponse, AgentListRow } from '../../../../NonReact/Types/AgentListTypes'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import { deleteACDAgentMany } from '../../../../NonReact/common/API/EntityAPIClient'
import { ConfigureAgentsModal } from '../../../../React/ConfigureAgents/Modal/ConfigureAgentsModal'
import { AdminListUrlBootstrap, parseAdminListUrlSearchParams } from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../Utils/adminListFilterReadiness'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'

const DEFAULT_AGENT_SORT: string = 'AgentNumber:ASC'

/**
 * Routed ACD Agents list view (partition-scoped).
 *
 * @returns ACD Agents list page content.
 */
export const AgentList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(AgentListLanguage.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [agentRows, setAgentRows]: ReactState<AgentListRow[]> = useState<AgentListRow[]>([])
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isAgentModalOpen, setAgentModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedAgentNumber, setSelectedAgentNumber]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [agentModalViewOnly, setAgentModalViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [telSysTypeID, setTelSysTypeID]: ReactState<Definable<number>> = useState<Definable<number>>()
    /** When opening Change/View, pass the selected row's partition so the modal uses it (e.g. when filter partition is ALL). */
    const [selectedRowPartIDInTelSys, setSelectedRowPartIDInTelSys]: ReactState<Definable<number>> =
        useState<Definable<number>>()

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const listCmeId: string = String(filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID)
    const listSearch: string = filters?.search ?? EMPTY_STRING

    const telSysRaw: Definable<string | number> = filters?.telephonyServer?.value
    const partRaw: Definable<string | number> = filters?.partition?.value
    const listTelSysId: Nullable<number> =
        isVarNotEmpty(telSysRaw) && telSysRaw !== ALL_VALUE ? Number(telSysRaw) : null
    const listPartitionId: Nullable<number> = isVarNotEmpty(partRaw) ? Number(partRaw) : null

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_AGENTS, filters, userInfo?.Scope?.CMEnvironmentID)

    /**
     * Loads ACD agent rows for the selected telephony server and partition.
     */
    const loadAgentList = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listTelSysId) || IsVarEmpty(listPartitionId)) {
            setAgentRows([])
            setTotalCount(0)
            setTelSysTypeID(undefined)
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            return
        }

        setLoading((n: number): number => n + 1)
        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch
        const sortExpr: string = sort ?? DEFAULT_AGENT_SORT

        getAgentList(listTelSysId, listPartitionId, false, pageStartIndex, rowsPerPage, sortExpr, searchQuery)
            .then((response: Nullable<AgentListResponse>): void => {
                if (!isMounted.current) return

                const data: Nullable<AgentListResponse['Data']> = response?.Data
                if (isVarNotEmpty(data) && isVarNotEmpty(data.TelSysTypeID)) {
                    setTelSysTypeID(data.TelSysTypeID)
                }
                const items: AgentListItem[] = data?.Items ?? []
                const rows: AgentListRow[] = items.map(
                    (item: AgentListItem, index: number): AgentListRow => ({
                        key: buildAdminListRowKey(
                            [
                                listTelSysId,
                                listPartitionId,
                                item.AgentGUID,
                                item.AgentNumber,
                                item.PartIDInTelSys,
                                item.IDOnTelSys
                            ],
                            pageStartIndex,
                            index
                        ),
                        AgentNumber: item.AgentNumber,
                        PartIDInTelSys: item.PartIDInTelSys,
                        OverrideMonitoring: item.OverrideMonitoring === true ? 1 : 0,
                        ListMonitoredDisplay: item.ListMonitoredDisplay ?? EMPTY_STRING,
                        Description: item.Description ?? EMPTY_STRING,
                        AgentNumberInternal: item.AgentNumberInternal ?? EMPTY_STRING,
                        ListPartitionNameDisplay: item.ListPartitionNameDisplay ?? EMPTY_STRING,
                        ListOmniChannelCategoryTypeDisplay: item.ListOmniChannelCategoryTypeDisplay ?? EMPTY_STRING
                    })
                )
                setAgentRows(rows)
                setTotalCount(data?.Total ?? 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)
                setHasResolvedInitialFiltersLoad(true)

                if (isMounted.current) setLoading((n: number): number => n - 1)
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listTelSysId,
        listPartitionId,
        listSearch,
        pagination,
        sort,
        setLoading,
        setTotalCount
    ])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    useEffect((): void => {
        loadAgentList()
    }, [loadAgentList])

    const refreshAgentList = useCallback((): void => {
        loadAgentList()
    }, [loadAgentList])

    const canOpenAgentModal: boolean = isVarNotEmpty(listTelSysId) && isVarNotEmpty(listPartitionId)

    const showInternalNumberColumn: boolean =
        telSysTypeID === TELEPHONY_SYSTEM_TYPE_BROADSOFT_BROADWORKS ||
        telSysTypeID === TELEPHONY_SYSTEM_TYPE_CISCO_WEBEX_CALLING

    const columns: DataTableColumn<AgentListRow, keyof AgentListRow>[] = useMemo((): DataTableColumn<
        AgentListRow,
        keyof AgentListRow
    >[] => {
        const base: DataTableColumn<AgentListRow, keyof AgentListRow>[] = [
            {
                headerName: language.get(AgentListLanguage.AgentList_Column_AgentNumber),
                field: 'AgentNumber',
                key: 'agent-list-agent-number',
                sortable: true
            },
            {
                headerName: language.get(AgentListLanguage.AgentList_Column_Description),
                field: 'Description',
                key: 'agent-list-description',
                sortable: true
            },
            {
                headerName: language.get(AgentListLanguage.AgentList_Column_Monitored),
                field: 'ListMonitoredDisplay',
                key: 'agent-list-monitored',
                sortable: true,
                renderCell: (row: AgentListRow): string =>
                    row.OverrideMonitoring === 1
                        ? language.get(AgentListLanguage.AgentList_Monitored_No)
                        : language.get(AgentListLanguage.AgentList_Monitored_Yes)
            },
            ...(showInternalNumberColumn
                ? [
                      {
                          headerName: language.get(AgentListLanguage.AgentList_Column_InternalNumber),
                          field: 'AgentNumberInternal',
                          key: 'agent-list-internal',
                          sortable: true
                      } as DataTableColumn<AgentListRow, keyof AgentListRow>
                  ]
                : []),
            {
                headerName: language.get(AgentListLanguage.AgentList_Column_PartitionName),
                field: 'ListPartitionNameDisplay',
                key: 'agent-list-partition-name',
                sortable: true,
                hideBelow: 'md',
                renderCell: (row: AgentListRow): string => row.ListPartitionNameDisplay ?? EMPTY_STRING
            }
        ]

        return base
    }, [language, showInternalNumberColumn])

    return (
        <div className='agent-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_agent} />
            <div className='agent-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        )
                    }}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    cmeFiltering={[[], (): boolean => true]}
                    onRefresh={loadAgentList}
                    selectFilterPlaceholderTooltips={{
                        [SelectFilterNames.TELEPHONY_SERVER]: {
                            show: true,
                            title: language.get(
                                GenericAdminFilterLanguage.GenericAdminFilterLanguage_PLEASE_SELECT_OPTION_TO_SHOW_RESULTS
                            )
                        }
                    }}
                    urlBootstrap={urlBootstrap}
                />
                <DataTable<AgentListRow, keyof AgentListRow>
                    permissionName={AdminBackendPermissionNames.AGENTS}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={agentRows}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad || isWaitingForInitialFiltersLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    onToolbarAddClick={(): void => {
                        if (!canOpenAgentModal) return

                        setSelectedAgentNumber(undefined)
                        setSelectedRowPartIDInTelSys(undefined)
                        setAgentModalViewOnly(false)
                        setAgentModalOpen(true)
                    }}
                    toolbarAddDisabled={!canOpenAgentModal}
                    toolbarAddDisabledTitle={language.get(AgentListLanguage.AgentList_AddButton_DisabledTooltip)}
                    onToolbarChangeClick={([selectedItem]: AgentListRow[]): void => {
                        if (
                            !canOpenAgentModal ||
                            IsVarEmpty(selectedItem) ||
                            !isStringNotEmpty(selectedItem.AgentNumber)
                        ) {
                            return
                        }

                        setSelectedAgentNumber(selectedItem.AgentNumber)
                        setSelectedRowPartIDInTelSys(selectedItem.PartIDInTelSys)
                        setAgentModalViewOnly(false)
                        setAgentModalOpen(true)
                    }}
                    onToolbarViewClick={([selectedItem]: AgentListRow[]): void => {
                        if (
                            !canOpenAgentModal ||
                            IsVarEmpty(selectedItem) ||
                            !isStringNotEmpty(selectedItem.AgentNumber)
                        ) {
                            return
                        }

                        setSelectedAgentNumber(selectedItem.AgentNumber)
                        setSelectedRowPartIDInTelSys(selectedItem.PartIDInTelSys)
                        setAgentModalViewOnly(true)
                        setAgentModalOpen(true)
                    }}
                    onToolbarDeleteClick={(selectedItems: AgentListRow[]): void => {
                        if (IsVarEmpty(listTelSysId) || IsVarEmpty(listPartitionId) || listPartitionId === ALL_VALUE) {
                            return
                        }

                        setLoading((n: number): number => n + 1)
                        deleteACDAgentMany(
                            listTelSysId,
                            listPartitionId,
                            selectedItems.map((row: AgentListRow): string => row.AgentNumber)
                        )
                            .then((): void => {
                                loadAgentList()
                            })
                            .catch((error: Error): void => {
                                displayError(error)
                            })
                            .finally((): void => {
                                if (isMounted.current) setLoading((n: number): number => n - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isAgentModalOpen && canOpenAgentModal && isVarNotEmpty(listTelSysId) ? (
                    <ConfigureAgentsModal
                        key={`${selectedAgentNumber ?? 'new'}-${agentModalViewOnly ? 'v' : 'e'}-${
                            selectedRowPartIDInTelSys ?? listPartitionId ?? 'all'
                        }`}
                        filters={{
                            cmeID: listCmeId,
                            telSysID: listTelSysId,
                            partIDInTelSys: selectedRowPartIDInTelSys ?? listPartitionId ?? undefined
                        }}
                        selectedAgent={selectedAgentNumber}
                        viewOnly={agentModalViewOnly}
                        refreshAction={refreshAgentList}
                        onModalExited={(): void => {
                            setAgentModalOpen(false)
                            setSelectedAgentNumber(undefined)
                            setSelectedRowPartIDInTelSys(undefined)
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
